import { createSelector } from '@ngrx/store';
import { AllowanceState } from '../allowance.state';
import { AllowanceParams } from '@app/pages/allowance/allowance';
import { SystemPreferenceCode } from '@app/shared/utils/enums/system-preference-code';
import { petHasGrowthChart } from '@app/shared/utils/static-helpers/growth-chart-helper';
import { Product, PetInfo } from '@app/core/models';
import { SpeciesCode, ReproductionStatusCode, getProgramFromPetInfoAndProducts, Programs, Helper } from '@app/shared/utils';
import { selectAllowance } from './allowance.selectors';

export const selectAllowanceParams = createSelector(
  selectAllowance,
  (state: AllowanceState) => state.allowanceParams || ({} as AllowanceParams),
);

export const selectNavigationRoutes = createSelector(selectAllowance, (state: AllowanceState) => state.allowanceNavigationRoutes);

export const selectProductSelectionIncentive = createSelector(selectAllowance, (state: AllowanceState) => state.productSelectionIncentive);

export const selectPetHasGrowthChart = (currentSystemPreferenceCode: SystemPreferenceCode) =>
  createSelector(selectAllowanceParams, (allowanceParams: AllowanceParams) => {
    return petHasGrowthChart(
      allowanceParams.deprecatedPetInfo.lifestage,
      allowanceParams.deprecatedPetInfo.speciesCode,
      allowanceParams.deprecatedPetInfo.targetWeight,
      currentSystemPreferenceCode,
    );
  });

export const selectIsNeuteredToday = createSelector(
  selectAllowanceParams,
  (allowanceParams) => allowanceParams.deprecatedPetInfo?.neuteredToday,
);

export const selectDeprecatedPetInfo = createSelector(
  selectAllowanceParams,
  (allowanceParams: AllowanceParams) => allowanceParams.deprecatedPetInfo,
);

export const selectIncentiveTool = createSelector(
  selectAllowanceParams,
  (allowanceParams: AllowanceParams) => allowanceParams.incentiveTool,
);

export const selectIsGestatingSpeciesNonWL = (selectedProducts: Product[], speciesCode: SpeciesCode) =>
  createSelector(selectDeprecatedPetInfo, (petInfo: PetInfo) => {
    return (
      petInfo?.speciesCode === speciesCode &&
      petInfo?.reproductionStatus === ReproductionStatusCode.Gestation &&
      getProgramFromPetInfoAndProducts(selectedProducts, petInfo) !== Programs.WEIGHT_LOSS
    );
  });

export const selectUnderweightAdult = (selectedProducts: Product[]) =>
  createSelector(selectDeprecatedPetInfo, (petInfo: PetInfo) => {
    const maxBcsUnderWeightAdult = 2;
    return (
      Helper.lifestageOld(petInfo.lifestage) && // isAdult
      petInfo.bcs <= maxBcsUnderWeightAdult &&
      petInfo.reproductionStatus !== ReproductionStatusCode.Gestation && // notGestating
      getProgramFromPetInfoAndProducts(selectedProducts, petInfo) !== Programs.WEIGHT_LOSS // notWL
    );
  });

export const selectNormalAdult = (selectedProducts: Product[]) =>
  createSelector(selectDeprecatedPetInfo, (petInfo: PetInfo) => {
    const maxBcsUnderWeightAdult = 2;
    return (
      Helper.lifestageOld(petInfo.lifestage) && // isAdult
      petInfo.bcs > maxBcsUnderWeightAdult &&
      getProgramFromPetInfoAndProducts(selectedProducts, petInfo) === Programs.NORMAL_ADULT && // adult program
      petInfo.reproductionStatus !== ReproductionStatusCode.Gestation && // notGestating
      getProgramFromPetInfoAndProducts(selectedProducts, petInfo) !== Programs.WEIGHT_LOSS // notWL
    );
  });

export const selectIsPetInLactation = createSelector(selectDeprecatedPetInfo, (petInfo: PetInfo) => {
  return petInfo?.reproductionStatus === ReproductionStatusCode.Lactation;
});
